import {
  Add,
  Cancel,
  CheckCircle,
  Close,
  ContentCut,
  EventBusyOutlined,
  InfoOutlined,
  WarningAmberOutlined,
} from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Autocomplete,
  Badge,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputAdornment,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { useFormik } from 'formik';
import { Fragment, useEffect, useRef, useState } from 'react';
import * as yup from 'yup';

import {
  Country,
  Disease,
  DiseaseStatusText,
  EDiseaseNoteStatus,
  EDiseaseStatus,
  ENextRound,
  ENextRoundVariant,
  ETypePets,
  ETypeVaccine,
  EUserRole,
  TDiseaseCountry,
  TDiseaseRound,
} from 'shared/api';
import { sanitizeRound, useCheckCodeUnique } from 'shared/lib';
import { AutocompleteCountry, SelectInput, VaccineAutocomplete } from 'shared/ui';

const validationSchema = yup.object({
  name: yup.string().required('Болезнь обязательное поле'),
  code: yup.string().required('Код болезни обязательное поле'),
  vaccine_list: yup.array().of(yup.object()),
  countries: yup.array().of(
    yup.object().shape({
      status: yup
        .mixed<EDiseaseStatus>()
        .oneOf(Object.values(EDiseaseStatus))
        .required('Статус для страны обязательное поле'),
      age_shedule: yup.array().of(
        yup.object().shape({
          start: yup
            .number()
            .typeError('Start must be a number')
            .min(0, 'Start must be ≥ 0')
            .required('Start is required'),
          end: yup.number().typeError('End must be a number').min(0, 'End must be ≥ 0').required('End is required'),
        }),
      ),
    }),
  ),
  descriptions: yup.string(),
  descriptions_en: yup.string(),
});

const initialRound: TDiseaseRound = {
  type_round: ENextRoundVariant.AnyDays,
  type_vaccination: ETypeVaccine.Vaccination,
  count_days: undefined,
  count_days2: undefined,
  count_years: undefined,
  isPeriodicRevactination: false,
};

const initialRoundReVaccination: TDiseaseRound = {
  ...initialRound,
  type_vaccination: ETypeVaccine.ReVaccination,
};

// only the last round of each period may be periodic — clears legacy conflicts
const normalizeCountriesPeriodic = (countries: TDiseaseCountry[]): TDiseaseCountry[] =>
  countries.map((c) => ({
    ...c,
    age_shedule: (c.age_shedule ?? []).map((period) => ({
      ...period,
      rounds: (period.rounds ?? []).map((r, i) =>
        i === period.rounds.length - 1 ? r : { ...r, isPeriodicRevactination: false },
      ),
    })),
  }));

const initialValues: Omit<Disease, '_id'> = {
  name: '',
  name_ru: '',
  code: '',
  vaccine_list: [],
  verified: false,
  isIncludePastDiseases: false,
  descriptions: undefined as unknown as string,
  descriptions_en: undefined as unknown as string,
  notes: undefined as unknown as string,
  countries: [],
};

const createCountrySettings = (countryCode: string, countryName: string) => ({
  status: EDiseaseStatus.Basic,
  countryCode,
  countryName,
  age_shedule: [
    {
      start: 0,
      end: 36500,
      rounds: [initialRound],
    },
  ],
  completed: false,
});

type EmptyPeriodInfo = {
  countryCode: string;
  index: number;
  start: number | string;
  end: number | string;
};

type EmptyRoundsConfirmState = {
  open: boolean;
  periods: EmptyPeriodInfo[];
  values: Omit<Disease, '_id'> | null;
};

type Props = {
  title: string;
  isOpen: boolean;
  isPets?: boolean;
  diseaseList: Disease[];
  selectedDisease?: Disease;
  countriesList: Country[];
  isLoading: boolean;
  userRole?: EUserRole;
  onVerified?: Function;
  onSubmit: Function;
  onClose: () => void;
  preselectedCountryCode?: string;
};

export const DiseaseDialog = ({
  title,
  isOpen,
  isPets = false,
  diseaseList,
  selectedDisease,
  countriesList,
  isLoading,
  userRole,
  onVerified,
  onClose,
  onSubmit,
  preselectedCountryCode,
}: Props) => {
  const [selectedAgeShedule, setSelectedAgeShedule] = useState(0);
  const [selectedCountryCode, setSelectedCountryCode] = useState('');
  const [selectedRound, setSelectedRound] = useState(0);
  const [isOpenCopyDialog, setIsOpenCopyDialog] = useState(false);
  const [emptyRoundsConfirm, setEmptyRoundsConfirm] = useState<EmptyRoundsConfirmState>({
    open: false,
    periods: [],
    values: null,
  });
  const countryRoundSectionRef = useRef<HTMLDivElement>(null);
  const countryNoteSectionRef = useRef<HTMLDivElement>(null);

  // Определяем режим: создание (true) или редактирование (false)
  const isCreateMode = !selectedDisease;

  const handleCountryChipClick = (countryCode: string) => {
    setSelectedCountryCode(countryCode);
    setSelectedAgeShedule(0);
    setTimeout(() => {
      countryRoundSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
  };

  const countryChipSx = (completed?: boolean) => ({
    color: completed ? '#29b194' : '',
    backgroundColor: 'transparent',
    margin: 0,
    height: 20,
    '& .MuiChip-label': { px: 0.5, fontSize: '0.8125rem' },
  });

  const submitValues = (values: Omit<Disease, '_id'>) => {
    const newCountries = values.countries.map((item) => ({
      ...item,
      age_shedule: item.age_shedule.map((shedule) => ({
        ...shedule,
        rounds: shedule.rounds.map(sanitizeRound),
      })),
    }));
    onSubmit({ ...values, countries: newCountries });
  };

  const formik = useFormik({
    initialValues: selectedDisease
      ? { ...selectedDisease, countries: normalizeCountriesPeriodic(selectedDisease.countries ?? []) }
      : initialValues,
    validationSchema,
    enableReinitialize: true,
    validateOnChange: false,
    validateOnBlur: true,
    onSubmit: (values: Omit<Disease, '_id'>, helpers) => {
      const emptyPeriods: EmptyPeriodInfo[] = values.countries.flatMap((c) =>
        c.age_shedule
          .map((p, index) => ({ countryCode: c.countryCode, index, start: p.start, end: p.end, rounds: p.rounds }))
          .filter((p) => (p.rounds?.length ?? 0) === 0)
          .map(({ countryCode, index, start, end }) => ({ countryCode, index, start, end })),
      );

      if (emptyPeriods.length > 0) {
        setEmptyRoundsConfirm({ open: true, periods: emptyPeriods, values });
        helpers.setSubmitting(false);
        return;
      }

      submitValues(values);
    },
  });

  const handleEmptyRoundsConfirm = () => {
    if (emptyRoundsConfirm.values) submitValues(emptyRoundsConfirm.values);
    setEmptyRoundsConfirm({ open: false, periods: [], values: null });
  };

  const scrollToCountryRoundSection = () => {
    setTimeout(() => {
      const node = countryRoundSectionRef.current;
      if (!node) return;
      const scrollParent = node.closest<HTMLElement>('.MuiDialog-paper');
      if (scrollParent) {
        const targetTop =
          scrollParent.scrollTop + node.getBoundingClientRect().top - scrollParent.getBoundingClientRect().top - 8;
        scrollParent.scrollTo({ top: Math.max(0, targetTop), behavior: 'smooth' });
      } else {
        node.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 50);
  };

  const handleEmptyRoundsCancel = () => {
    const first = emptyRoundsConfirm.periods[0];
    setEmptyRoundsConfirm({ open: false, periods: [], values: null });
    if (first) {
      setSelectedCountryCode(first.countryCode);
      setSelectedAgeShedule(first.index);
      scrollToCountryRoundSection();
    }
  };

  const handleGoToEmptyPeriod = (countryCode: string, index: number) => {
    setSelectedCountryCode(countryCode);
    setSelectedAgeShedule(index);
    setEmptyRoundsConfirm({ open: false, periods: [], values: null });
    scrollToCountryRoundSection();
  };

  const getSelectedCountryValue = () =>
    formik.values.countries.find(({ countryCode }) => countryCode === selectedCountryCode);

  const codeCheck = useCheckCodeUnique(
    isPets ? 'diseasePets' : 'disease',
    formik.values.code,
    selectedDisease?._id,
    isOpen && formik.values.code.trim().length > 0,
  );

  const isExistDiseaseName = (() => {
    const foundDisease = diseaseList.find(({ name }) => name === formik.values.name);
    // Если болезнь не найдена - имя уникально
    if (!foundDisease) return false;
    // Если это та же болезнь (редактирование) - имя уникально
    if (selectedDisease && foundDisease._id === selectedDisease._id) return false;
    // Иначе - имя уже занято другой болезнью
    return true;
  })();

  const handleAddRound = () => {
    const selectedCountry = getSelectedCountryValue();

    if (!selectedCountry) return;

    const lastRound = selectedCountry.age_shedule[selectedAgeShedule]?.rounds.at(-1);
    const seed = lastRound?.type_vaccination === ETypeVaccine.ReVaccination ? initialRoundReVaccination : initialRound;

    const newData = formik.values.countries.map((item) =>
      item.countryCode === selectedCountryCode
        ? {
            ...item,
            age_shedule: item.age_shedule.map((shedule, index) =>
              selectedAgeShedule === index ? { ...shedule, rounds: [...shedule.rounds, seed] } : shedule,
            ),
          }
        : item,
    );

    void formik.setFieldValue('countries', newData);
    setSelectedRound(selectedCountry.age_shedule[selectedAgeShedule].rounds.length);
  };

  const handleDeleteRound = () => {
    const currentCountry = getSelectedCountryValue();
    if (!currentCountry) return;

    const newData = formik.values.countries.map((item) =>
      item.countryCode === selectedCountryCode
        ? {
            ...item,
            age_shedule: item.age_shedule.map((shedule, index) => {
              if (index !== selectedAgeShedule) return shedule;
              const filtered = shedule.rounds.filter((_, i) => i !== selectedRound);
              // First round cannot be ReVaccination — flip it to Vaccination
              const rounds = filtered.map((r, i) =>
                i === 0 && r.type_vaccination === ETypeVaccine.ReVaccination
                  ? sanitizeRound({ ...r, type_vaccination: ETypeVaccine.Vaccination })
                  : r,
              );
              return { ...shedule, rounds };
            }),
          }
        : item,
    );
    void formik.setFieldValue('countries', newData);

    const newLen = (currentCountry.age_shedule[selectedAgeShedule]?.rounds.length ?? 1) - 1;
    setSelectedRound((prev) => Math.max(0, Math.min(newLen - 1, prev)));
  };

  const handleChangeCountry = (_: any, v: any) => setSelectedCountryCode(v?.code ?? '');

  const handleChangeCountryData = (fieldName: string, value: any) => {
    const isExistCountryData = getSelectedCountryValue();

    if (isExistCountryData) {
      const list = formik.values.countries.map((item) => {
        if (
          fieldName === 'completed' ||
          fieldName === 'status' ||
          fieldName === 'note' ||
          fieldName === 'note_status'
        ) {
          return item.countryCode === selectedCountryCode ? { ...item, [fieldName]: value } : item;
        }

        return item.countryCode === selectedCountryCode
          ? {
              ...item,
              age_shedule: item.age_shedule.map((shedule, sheduleIndex) => {
                if (fieldName === 'start' || fieldName === 'end') {
                  return selectedAgeShedule === sheduleIndex ? { ...shedule, [fieldName]: value } : shedule;
                } else {
                  const rounds = shedule.rounds.map((round, index) =>
                    selectedRound === index ? sanitizeRound({ ...round, [fieldName]: value }) : round,
                  );

                  const newShedule = {
                    ...shedule,
                    rounds,
                  };

                  return selectedAgeShedule === sheduleIndex ? newShedule : shedule;
                }
              }),
            }
          : item;
      });

      void formik.setFieldValue('countries', list);
      return;
    }

    // создаем новый блок для страны если еще не было добавлено никаких данных
    const countryName = countriesList.find((c) => c.code === selectedCountryCode)?.name ?? selectedCountryCode;
    const newCountryData = createCountrySettings(selectedCountryCode, countryName);

    void formik.setFieldValue('countries', [...formik.values.countries, { ...newCountryData, [fieldName]: value }]);
  };

  const handleClearCountrySettings = () => {
    const newCountries = formik.values.countries.filter(({ countryCode }) => countryCode !== selectedCountryCode);

    void formik.setFieldValue('countries', newCountries);
  };

  const handleToggleCompletedCountrySettings = () => {
    const completed = getSelectedCountryValue()?.completed;
    handleChangeCountryData('completed', !completed);
  };

  const handleOpenCopyCountrySettings = () => setIsOpenCopyDialog(true);

  const handleCopyCoutrySettings = (settings: TDiseaseCountry | undefined) => {
    if (!settings) return;

    const sanitized: TDiseaseCountry = {
      ...settings,
      age_shedule: settings.age_shedule.map((s) => ({
        ...s,
        rounds: s.rounds.map(sanitizeRound),
      })),
    };

    const isExist = formik.values.countries.find((item) => item.countryCode === sanitized.countryCode);

    if (isExist) {
      void formik.setFieldValue('countries', [
        ...formik.values.countries.filter((item) => item.countryCode !== sanitized.countryCode),
        sanitized,
      ]);
    } else {
      void formik.setFieldValue('countries', [...formik.values.countries, sanitized]);
    }
  };

  const handleAddAgeShedule = () => {
    const currentCountry = getSelectedCountryValue();

    if (currentCountry) {
      const lastPeriod = currentCountry.age_shedule.at(-1);

      void formik.setFieldValue('countries', [
        ...formik.values.countries.filter((item) => item.countryCode !== selectedCountryCode),
        {
          ...currentCountry,
          age_shedule: [
            ...currentCountry.age_shedule,
            {
              start: lastPeriod?.end ? +lastPeriod?.end + 1 : '0',
              end: lastPeriod?.end ? +lastPeriod?.end + 2 : '0',
              rounds: [initialRound],
            },
          ],
        },
      ]);
      setSelectedAgeShedule(selectedAgeShedule + 1);
    } else {
      const countryName = countriesList.find((c) => c.code === selectedCountryCode)?.name ?? selectedCountryCode;
      const newCountryData = createCountrySettings(selectedCountryCode, countryName);
      void formik.setFieldValue('countries', [...formik.values.countries, newCountryData]);
      setSelectedAgeShedule(0);
    }
  };

  const handleDeleteAgeShedule = () => {
    const currentCountry = getSelectedCountryValue();
    if (!currentCountry) return;
    const list = currentCountry.age_shedule.map((s) => ({ ...s }));
    if (selectedAgeShedule <= 0 || selectedAgeShedule >= list.length) return;

    const hasNext = selectedAgeShedule < list.length - 1;
    if (hasNext) {
      list[selectedAgeShedule - 1] = { ...list[selectedAgeShedule - 1], end: list[selectedAgeShedule].end };
    }
    list.splice(selectedAgeShedule, 1);

    updateSelectedCountryAgeShedule(() => list);
    setSelectedAgeShedule((prev) => Math.max(0, prev - 1));
  };

  const updateSelectedCountryAgeShedule = (
    updater: (list: TDiseaseCountry['age_shedule']) => TDiseaseCountry['age_shedule'],
  ) => {
    void formik.setFieldValue(
      'countries',
      formik.values.countries.map((item) =>
        item.countryCode === selectedCountryCode ? { ...item, age_shedule: updater(item.age_shedule) } : item,
      ),
    );
  };

  const canInsertAgeShedule = (atIndex: number) => {
    const list = getSelectedCountryValue()?.age_shedule;
    if (!list) return false;
    const prev = list[atIndex - 1];
    const next = list[atIndex];
    if (!prev || !next) return false;
    const prevLen = +prev.end - +prev.start;
    const nextLen = +next.end - +next.start;
    if (prevLen < 2 || nextLen < 2) return false;
    const prevMid = +prev.start + Math.floor(prevLen / 2);
    const nextMid = +next.start + Math.ceil(nextLen / 2);
    return prevMid + 1 <= nextMid - 1;
  };

  const handleInsertAgeShedule = (atIndex: number) => {
    const currentCountry = getSelectedCountryValue();
    if (!currentCountry) return;
    const list = currentCountry.age_shedule;
    const prev = list[atIndex - 1];
    const next = list[atIndex];
    if (!prev || !next) return;

    const prevLen = +prev.end - +prev.start;
    const nextLen = +next.end - +next.start;
    if (prevLen < 2 || nextLen < 2) return;

    const prevMid = +prev.start + Math.floor(prevLen / 2);
    const nextMid = +next.start + Math.ceil(nextLen / 2);
    const newPeriod = { start: prevMid + 1, end: nextMid - 1, rounds: [initialRound] };
    if (newPeriod.start > newPeriod.end) return;

    const updated = list.map((s) => ({ ...s }));
    updated[atIndex - 1] = { ...updated[atIndex - 1], end: prevMid };
    updated[atIndex] = { ...updated[atIndex], start: nextMid };
    updated.splice(atIndex, 0, newPeriod);

    updateSelectedCountryAgeShedule(() => updated);
    setSelectedAgeShedule(atIndex);
    setSelectedRound(0);
  };

  const handleSplitAgeShedule = (index: number) => {
    const currentCountry = getSelectedCountryValue();
    if (!currentCountry) return;
    const period = currentCountry.age_shedule[index];
    if (!period) return;

    const start = +period.start;
    const end = +period.end;
    if (end - start < 1) return;

    const mid = Math.floor((start + end) / 2);
    const firstHalf = { ...period, start, end: mid };
    const secondHalf = { start: mid + 1, end, rounds: [initialRound] };

    const updated = [...currentCountry.age_shedule];
    updated.splice(index, 1, firstHalf, secondHalf);

    updateSelectedCountryAgeShedule(() => updated);
    setSelectedAgeShedule(index);
    setSelectedRound(0);
  };

  const handleInsertRound = (atIndex: number) => {
    const currentRounds = getSelectedCountryValue()?.age_shedule[selectedAgeShedule]?.rounds ?? [];
    const priorIsReVaccination = currentRounds
      .slice(0, atIndex)
      .some((r) => r.type_vaccination === ETypeVaccine.ReVaccination);
    const seed = priorIsReVaccination ? initialRoundReVaccination : initialRound;

    updateSelectedCountryAgeShedule((list) =>
      list.map((shedule, i) => {
        if (i !== selectedAgeShedule) return shedule;
        const rounds = [...shedule.rounds];
        rounds.splice(atIndex, 0, seed);
        return { ...shedule, rounds };
      }),
    );
    setSelectedRound(atIndex);
  };

  const handleAgeSheduleStartChange = (index: number, value: number) => {
    if (isNaN(value) || value < 0) return;
    const currentCountry = getSelectedCountryValue();
    if (!currentCountry) return;
    const list = currentCountry.age_shedule.map((s) => ({ ...s }));
    list[index].start = value;
    updateSelectedCountryAgeShedule(() => list);
  };

  const handleAgeSheduleEndChange = (index: number, value: number) => {
    if (isNaN(value) || value < 0) return;
    const currentCountry = getSelectedCountryValue();
    if (!currentCountry) return;
    const list = currentCountry.age_shedule.map((s) => ({ ...s }));
    list[index].end = value;
    updateSelectedCountryAgeShedule(() => list);
  };

  const handleAgeSheduleStartBlur = (index: number) => {
    const currentCountry = getSelectedCountryValue();
    if (!currentCountry) return;
    const list = currentCountry.age_shedule.map((s) => ({ ...s }));
    const val = +list[index].start;
    const minStart = index > 0 ? +list[index - 1].start + 1 : 0;
    const maxStart = +list[index].end;
    const clamped = isNaN(val) ? minStart : Math.max(minStart, Math.min(maxStart, val));

    list[index].start = clamped;
    if (index > 0) {
      list[index - 1] = { ...list[index - 1], end: clamped - 1 };
    }
    updateSelectedCountryAgeShedule(() => list);
  };

  const handleAgeSheduleEndBlur = (index: number) => {
    const currentCountry = getSelectedCountryValue();
    if (!currentCountry) return;
    const list = currentCountry.age_shedule.map((s) => ({ ...s }));
    const val = +list[index].end;
    const minEnd = +list[index].start;
    const maxEnd = index < list.length - 1 ? +list[index + 1].end - 1 : Infinity;
    const clamped = isNaN(val) ? minEnd : Math.max(minEnd, Math.min(maxEnd, val));

    list[index].end = clamped;
    if (index < list.length - 1) {
      list[index + 1] = { ...list[index + 1], start: clamped + 1 };
    }
    updateSelectedCountryAgeShedule(() => list);
  };

  useEffect(() => {
    if (isOpen) {
      formik.resetForm({
        values: selectedDisease
          ? { ...selectedDisease, countries: normalizeCountriesPeriodic(selectedDisease.countries ?? []) }
          : initialValues,
      });
      codeCheck.reset();
    } else {
      setSelectedCountryCode('');
      setSelectedAgeShedule(0);
      setSelectedRound(0);
      setEmptyRoundsConfirm({ open: false, periods: [], values: null });
    }
  }, [isOpen, selectedDisease]);

  useEffect(() => {
    if (isOpen && preselectedCountryCode) {
      setSelectedCountryCode(preselectedCountryCode);
      // Скролл к Country note (если есть) — иначе к секции Round for country
      setTimeout(() => {
        const target = countryNoteSectionRef.current ?? countryRoundSectionRef.current;
        target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
    }
  }, [isOpen, preselectedCountryCode]);

  useEffect(() => setSelectedRound(0), [selectedCountryCode, selectedAgeShedule]);

  return (
    <Fragment>
      <Dialog
        open={isOpen}
        fullWidth
        maxWidth={false}
        PaperProps={{
          sx: {
            width: 'min(1000px, 70vw)',
            maxHeight: '85vh',
          },
        }}
      >
        <form onSubmit={formik.handleSubmit}>
          <DialogTitle>{title}</DialogTitle>
          <DialogContent>
            <TextField
              label="Name (EN)"
              fullWidth
              margin="dense"
              name="name"
              value={formik.values.name ?? selectedDisease?.name}
              onChange={formik.handleChange}
              error={isExistDiseaseName}
              helperText={isExistDiseaseName && 'This disease has already been added!'}
            />
            <TextField
              label="Name (RU)"
              fullWidth
              margin="dense"
              name="name_ru"
              value={formik.values.name_ru ?? selectedDisease?.name_ru}
              onChange={formik.handleChange}
              error={isExistDiseaseName}
              helperText={isExistDiseaseName && 'This disease has already been added!'}
            />
            <TextField
              label="Code (EN)"
              fullWidth
              margin="dense"
              name="code"
              value={formik.values.code}
              onChange={formik.handleChange}
              error={codeCheck.isDuplicate}
              helperText={codeCheck.isDuplicate ? 'This code is already in use' : undefined}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {codeCheck.isChecking && <CircularProgress size={20} />}
                    {codeCheck.isUnique && <CheckCircle color="success" />}
                    {codeCheck.isDuplicate && <Cancel color="error" />}
                  </InputAdornment>
                ),
              }}
            />
            {isPets && (
              <FormControl fullWidth margin="dense">
                <InputLabel>Type</InputLabel>
                <Select
                  label="Type"
                  value={formik.values.type_pets}
                  defaultValue={selectedDisease?.type_pets}
                  name="type_pets"
                  onChange={formik.handleChange}
                >
                  {Object.entries(ETypePets).map(([key, value]) => (
                    <MenuItem key={key} value={value}>
                      {key}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <VaccineAutocomplete
              label="Suitable vaccines"
              name="vaccine_list"
              values={formik.values.vaccine_list}
              onChange={formik.setFieldValue}
              isPets={isPets}
              error={formik.touched.vaccine_list && Boolean(formik.errors.vaccine_list)}
              helperText={formik.touched.vaccine_list && (formik.errors.vaccine_list as string)}
            />
            {formik.values.countries.filter((item) => item.status === EDiseaseStatus.Basic).length > 0 && (
              <Autocomplete
                fullWidth
                readOnly
                multiple
                value={[...formik.values.countries.filter((item) => item.status === EDiseaseStatus.Basic)].sort(
                  (a, b) => a.countryName.localeCompare(b.countryName),
                )}
                options={[...formik.values.countries.filter((item) => item.status === EDiseaseStatus.Basic)].sort(
                  (a, b) => a.countryName.localeCompare(b.countryName),
                )}
                getOptionLabel={(option) => option.countryName}
                renderInput={(params) => {
                  return <TextField {...params} margin="dense" label="Basic vaccination calendar of countries" />;
                }}
                renderTags={(tagValue, getTagProps) =>
                  tagValue.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={`${index}`}
                      label={option.countryName}
                      size="small"
                      variant="filled"
                      clickable
                      onClick={() => handleCountryChipClick(option.countryCode)}
                      sx={countryChipSx(option.completed)}
                    />
                  ))
                }
                sx={{
                  '& .MuiAutocomplete-endAdornment': {
                    display: 'none',
                  },
                }}
              />
            )}
            {formik.values.countries.filter((item) => item.status === EDiseaseStatus.Mandatory).length > 0 && (
              <Autocomplete
                fullWidth
                readOnly
                multiple
                value={[...formik.values.countries.filter((item) => item.status === EDiseaseStatus.Mandatory)].sort(
                  (a, b) => a.countryName.localeCompare(b.countryName),
                )}
                options={[...formik.values.countries.filter((item) => item.status === EDiseaseStatus.Mandatory)].sort(
                  (a, b) => a.countryName.localeCompare(b.countryName),
                )}
                getOptionLabel={(option) => option.countryName}
                renderInput={(params) => {
                  return (
                    <TextField {...params} margin="dense" label="In the national vaccination calendar of countries" />
                  );
                }}
                renderTags={(tagValue, getTagProps) =>
                  tagValue.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={`${index}`}
                      label={option?.countryName}
                      size="small"
                      variant="filled"
                      clickable
                      onClick={() => handleCountryChipClick(option.countryCode)}
                      sx={countryChipSx(option?.completed)}
                    />
                  ))
                }
                sx={{
                  '& .MuiAutocomplete-endAdornment': {
                    display: 'none',
                  },
                }}
              />
            )}
            {formik.values.countries.filter((item) => item.status === EDiseaseStatus.Additional).length > 0 && (
              <Autocomplete
                fullWidth
                readOnly
                multiple
                value={[...formik.values.countries.filter((item) => item.status === EDiseaseStatus.Additional)].sort(
                  (a, b) => a.countryName.localeCompare(b.countryName),
                )}
                options={[...formik.values.countries.filter((item) => item.status === EDiseaseStatus.Additional)].sort(
                  (a, b) => a.countryName.localeCompare(b.countryName),
                )}
                getOptionLabel={(option) => option.countryName}
                renderInput={(params) => {
                  return <TextField {...params} margin="dense" label="Additional vaccination calendar of countries" />;
                }}
                renderTags={(tagValue, getTagProps) =>
                  tagValue.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={`${index}`}
                      label={option?.countryName}
                      size="small"
                      variant="filled"
                      clickable
                      onClick={() => handleCountryChipClick(option.countryCode)}
                      sx={countryChipSx(option?.completed)}
                    />
                  ))
                }
                sx={{
                  '& .MuiAutocomplete-endAdornment': {
                    display: 'none',
                  },
                }}
              />
            )}
            {formik.values.countries.filter((item) => item.status === EDiseaseStatus.Epidemic).length > 0 && (
              <Autocomplete
                fullWidth
                readOnly
                multiple
                value={[...formik.values.countries.filter((item) => item.status === EDiseaseStatus.Epidemic)].sort(
                  (a, b) => a.countryName.localeCompare(b.countryName),
                )}
                options={[...formik.values.countries.filter((item) => item.status === EDiseaseStatus.Epidemic)].sort(
                  (a, b) => a.countryName.localeCompare(b.countryName),
                )}
                getOptionLabel={(option) => option.countryName}
                renderInput={(params) => {
                  return <TextField {...params} margin="dense" label="Epidemic vaccination calendar of countries" />;
                }}
                renderTags={(tagValue, getTagProps) =>
                  tagValue.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={`${index}`}
                      label={option?.countryName}
                      size="small"
                      variant="filled"
                      clickable
                      onClick={() => handleCountryChipClick(option.countryCode)}
                      sx={countryChipSx(option?.completed)}
                    />
                  ))
                }
                sx={{
                  '& .MuiAutocomplete-endAdornment': {
                    display: 'none',
                  },
                }}
              />
            )}

            <Box ref={countryRoundSectionRef} sx={{ backgroundColor: 'rgba(41,177,148, 0.15)' }} p={2}>
              <Typography variant="body2" fontWeight="bold" mb={1}>
                Round for country
              </Typography>
              <Box display="flex" justifyContent="space-between">
                <AutocompleteCountry
                  key={selectedCountryCode}
                  label="Country"
                  value={selectedCountryCode}
                  countries={countriesList}
                  compareList={formik.values.countries}
                  onChange={handleChangeCountry}
                  sx={{ width: '73%' }}
                />
                <Button
                  variant="contained"
                  color={!getSelectedCountryValue()?.completed ? 'primary' : 'error'}
                  sx={{ width: '25%' }}
                  onClick={handleToggleCompletedCountrySettings}
                  disabled={!getSelectedCountryValue()?.status}
                >
                  {!getSelectedCountryValue()?.completed ? 'Completed' : 'Uncompleted'}
                </Button>
              </Box>
              {selectedCountryCode && (
                <Box mt={1}>
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Button variant="text" color="primary" size="small" onClick={handleOpenCopyCountrySettings}>
                      Copy from
                    </Button>
                    {getSelectedCountryValue() && (
                      <Button
                        variant="outlined"
                        startIcon={<Close />}
                        color="error"
                        size="small"
                        onClick={handleClearCountrySettings}
                      >
                        Delete setting country
                      </Button>
                    )}
                  </Box>
                  {getSelectedCountryValue() && (
                    <FormControl
                      fullWidth
                      margin="dense"
                      sx={{ marginBottom: '16px' }}
                      error={!getSelectedCountryValue()?.status}
                    >
                      <InputLabel>Status*</InputLabel>
                      <Select
                        label="Status*"
                        value={getSelectedCountryValue()?.status}
                        name={'status'}
                        onChange={(e: any) => handleChangeCountryData('status', e.target.value)}
                      >
                        {Object.values(EDiseaseStatus).map((status) => (
                          <MenuItem key={status} value={status}>
                            {DiseaseStatusText[status]}
                          </MenuItem>
                        ))}
                      </Select>
                      {!getSelectedCountryValue()?.status && <FormHelperText>Required field</FormHelperText>}
                    </FormControl>
                  )}
                  <Box>
                    <Typography fontWeight="bold" mt={1}>
                      Age Shedule
                    </Typography>
                    <Box display="flex" alignItems="center" flexWrap="wrap" my={1} rowGap={1}>
                      {getSelectedCountryValue()?.age_shedule.map((item, index) => (
                        <Box key={`${index}_${item.start}_age_shedule_chip`} display="flex" alignItems="center">
                          {index > 0 &&
                            (() => {
                              const canInsert = canInsertAgeShedule(index);
                              return (
                                <Tooltip
                                  title={canInsert ? 'Insert period between' : 'Periods too small to split'}
                                  arrow
                                >
                                  <Box
                                    sx={{
                                      width: 24,
                                      height: 32,
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      cursor: canInsert ? 'pointer' : 'not-allowed',
                                      zIndex: 1,
                                      borderRadius: '50%',
                                      '& .insert-icon': {
                                        opacity: canInsert ? 0.3 : 0.15,
                                        transition: 'all 0.2s',
                                        fontSize: 16,
                                        color: canInsert ? '#29b194' : 'rgba(0, 0, 0, 0.4)',
                                      },
                                      '&:hover .insert-icon': canInsert ? { opacity: 1, transform: 'scale(1.3)' } : {},
                                    }}
                                    onClick={canInsert ? () => handleInsertAgeShedule(index) : undefined}
                                  >
                                    <Add className="insert-icon" />
                                  </Box>
                                </Tooltip>
                              );
                            })()}
                          <Badge
                            invisible={(item.rounds?.length ?? 0) > 0}
                            overlap="rectangular"
                            badgeContent={
                              <Tooltip title="No rounds in this period" arrow>
                                <Box
                                  component="span"
                                  sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: 16,
                                    height: 16,
                                    borderRadius: '50%',
                                    backgroundColor: '#f59e0b',
                                    color: 'white',
                                    fontSize: 11,
                                    fontWeight: 700,
                                    lineHeight: 1,
                                    cursor: 'help',
                                    boxShadow: '0 0 0 2px rgba(245, 236, 219, 1)',
                                  }}
                                >
                                  !
                                </Box>
                              </Tooltip>
                            }
                            sx={{
                              '& .MuiBadge-badge': {
                                padding: 0,
                                minWidth: 0,
                                height: 'auto',
                                backgroundColor: 'transparent',
                                right: 4,
                                top: 4,
                              },
                            }}
                          >
                            <Chip
                              label={`${item.start}-${item.end}`}
                              onClick={() => setSelectedAgeShedule(index)}
                              sx={{
                                backgroundColor: selectedAgeShedule === index ? '#29b194' : 'unset',
                                color: selectedAgeShedule === index ? 'white' : '',
                                borderRadius: '6px',
                                border:
                                  selectedAgeShedule !== index
                                    ? (item.rounds?.length ?? 0) === 0
                                      ? '1px dashed rgba(0, 0, 0, 0.4)'
                                      : '1px solid rgba(0, 0, 0, 0.3)'
                                    : '',
                              }}
                              clickable={selectedAgeShedule !== index}
                            />
                          </Badge>
                        </Box>
                      ))}
                      {getSelectedCountryValue()?.age_shedule?.length === 0 && (
                        <Typography>Add first shedule</Typography>
                      )}
                      <Tooltip title="Add period at end" arrow>
                        <Button
                          variant="contained"
                          size="small"
                          sx={{
                            ml: (getSelectedCountryValue()?.age_shedule?.length ?? 0) > 0 ? '4px' : 0,
                            px: 0.5,
                            minWidth: 'auto',
                            color: 'rgba(58, 58, 58, 1)',
                            backgroundColor: 'rgba(0, 0, 0, 0.05)',
                            boxShadow: 'none',
                            ':hover': { backgroundColor: 'rgba(0, 0, 0, 0.07)', boxShadow: 'none' },
                          }}
                          onClick={handleAddAgeShedule}
                        >
                          <Add />
                        </Button>
                      </Tooltip>
                    </Box>
                    {(getSelectedCountryValue()?.age_shedule?.length ?? 0) > 0 && (
                      <Box mb={2}>
                        <Box display="flex" justifyContent="space-between">
                          <TextField
                            sx={{ width: '49%' }}
                            margin="dense"
                            label="Start (in days)*"
                            type="number"
                            name={`start`}
                            value={getSelectedCountryValue()?.age_shedule[selectedAgeShedule]?.start ?? ''}
                            onChange={(e: any) => {
                              if (e.target.value === '') {
                                handleChangeCountryData('start', '');
                              } else {
                                handleAgeSheduleStartChange(selectedAgeShedule, Number(e.target.value));
                              }
                            }}
                            onBlur={() => handleAgeSheduleStartBlur(selectedAgeShedule)}
                            onKeyDown={(e) =>
                              e.key === 'Enter' && (e.preventDefault(), (e.target as HTMLElement).blur())
                            }
                            error={
                              `${getSelectedCountryValue()?.age_shedule[selectedAgeShedule]?.start ?? ''}`.length === 0
                            }
                            helperText={
                              `${getSelectedCountryValue()?.age_shedule[selectedAgeShedule]?.start ?? ''}`.length ===
                                0 && 'Field is required!'
                            }
                          />
                          <TextField
                            sx={{ width: '49%' }}
                            margin="dense"
                            label="End (in days)*"
                            type="number"
                            name={`end`}
                            value={getSelectedCountryValue()?.age_shedule[selectedAgeShedule]?.end ?? ''}
                            onChange={(e: any) => {
                              if (e.target.value === '') {
                                handleChangeCountryData('end', '');
                              } else {
                                handleAgeSheduleEndChange(selectedAgeShedule, Number(e.target.value));
                              }
                            }}
                            onBlur={() => handleAgeSheduleEndBlur(selectedAgeShedule)}
                            onKeyDown={(e) =>
                              e.key === 'Enter' && (e.preventDefault(), (e.target as HTMLElement).blur())
                            }
                            error={
                              `${getSelectedCountryValue()?.age_shedule[selectedAgeShedule]?.end ?? ''}`.length === 0
                            }
                            helperText={
                              `${getSelectedCountryValue()?.age_shedule[selectedAgeShedule]?.end ?? ''}`.length === 0 &&
                              'Field is required!'
                            }
                          />
                        </Box>
                        <Box display="flex" justifyContent={selectedAgeShedule !== 0 ? 'space-between' : 'flex-end'}>
                          {selectedAgeShedule !== 0 && (
                            <Button color="error" size="small" onClick={handleDeleteAgeShedule}>
                              DELETE THIS AGE
                            </Button>
                          )}
                          {+(getSelectedCountryValue()?.age_shedule[selectedAgeShedule]?.end ?? 0) -
                            +(getSelectedCountryValue()?.age_shedule[selectedAgeShedule]?.start ?? 0) >=
                            1 && (
                            <Button
                              size="small"
                              startIcon={<ContentCut sx={{ fontSize: 14 }} />}
                              onClick={() => handleSplitAgeShedule(selectedAgeShedule)}
                              sx={{ color: '#29b194' }}
                            >
                              SPLIT PERIOD
                            </Button>
                          )}
                        </Box>
                      </Box>
                    )}
                    <Box display="flex" alignItems="center" flexWrap="wrap" rowGap={1}>
                      {getSelectedCountryValue()?.age_shedule[selectedAgeShedule]?.rounds.map((round, index) => (
                        <Box key={index} display="flex" alignItems="center">
                          {index > 0 && (
                            <Tooltip title="Insert round between" arrow>
                              <Box
                                sx={{
                                  width: 24,
                                  height: 32,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  cursor: 'pointer',
                                  zIndex: 1,
                                  borderRadius: '50%',
                                  '& .insert-icon': {
                                    opacity: 0.3,
                                    transition: 'all 0.2s',
                                    fontSize: 16,
                                    color: '#29b194',
                                  },
                                  '&:hover .insert-icon': {
                                    opacity: 1,
                                    transform: 'scale(1.3)',
                                  },
                                }}
                                onClick={() => handleInsertRound(index)}
                              >
                                <Add className="insert-icon" />
                              </Box>
                            </Tooltip>
                          )}
                          <Chip
                            label={`Round ${index + 1}`}
                            onClick={() => setSelectedRound(index)}
                            sx={{
                              backgroundColor: selectedRound === index ? '#29b194' : 'unset',
                              color: selectedRound === index ? 'white' : '',
                              borderRadius: '6px',
                              border: selectedRound !== index ? '1px solid rgba(0, 0, 0, 0.3)' : '',
                            }}
                            clickable={selectedRound !== index}
                          />
                        </Box>
                      ))}
                      {!(
                        getSelectedCountryValue()?.age_shedule[selectedAgeShedule]?.rounds.at(-1)?.type_vaccination ===
                          ETypeVaccine.ReVaccination &&
                        getSelectedCountryValue()?.age_shedule[selectedAgeShedule]?.rounds.at(-1)
                          ?.isPeriodicRevactination
                      ) &&
                        (getSelectedCountryValue()?.age_shedule[selectedAgeShedule]?.rounds.length ?? 0) > 0 && (
                          <Tooltip title="Add round at end" arrow>
                            <Button
                              variant="contained"
                              size="small"
                              sx={{
                                ml:
                                  (getSelectedCountryValue()?.age_shedule[selectedAgeShedule]?.rounds.length ?? 0) > 0
                                    ? '4px'
                                    : 0,
                                px: 0.5,
                                minWidth: 'auto',
                                color: 'rgba(58, 58, 58, 1)',
                                backgroundColor: 'rgba(0, 0, 0, 0.05)',
                                boxShadow: 'none',
                                ':hover': { backgroundColor: 'rgba(0, 0, 0, 0.07)', boxShadow: 'none' },
                              }}
                              onClick={handleAddRound}
                            >
                              <Add />
                            </Button>
                          </Tooltip>
                        )}
                    </Box>
                    {(getSelectedCountryValue()?.age_shedule[selectedAgeShedule]?.rounds.length ?? 0) === 0 &&
                      !!getSelectedCountryValue() && (
                        <Box
                          mt={2}
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 1,
                            minHeight: 140,
                            borderRadius: '6px',
                            border: '1px dashed rgba(41, 177, 148, 0.5)',
                            backgroundColor: 'rgba(255, 255, 255, 0.4)',
                          }}
                        >
                          <EventBusyOutlined sx={{ fontSize: 28, color: 'rgba(0, 0, 0, 0.35)' }} />
                          <Typography variant="body2" sx={{ color: 'rgba(0, 0, 0, 0.7)' }}>
                            No rounds in this period
                          </Typography>
                          <Button
                            variant="text"
                            size="small"
                            startIcon={<Add sx={{ fontSize: 16 }} />}
                            onClick={handleAddRound}
                            sx={{
                              color: '#29b194',
                              textTransform: 'uppercase',
                              letterSpacing: '0.04em',
                              ':hover': { backgroundColor: 'rgba(41,177,148,0.08)' },
                            }}
                          >
                            Add round
                          </Button>
                        </Box>
                      )}
                    {getSelectedCountryValue()?.age_shedule[selectedAgeShedule]?.rounds[selectedRound] &&
                      [getSelectedCountryValue()?.age_shedule[selectedAgeShedule]?.rounds[selectedRound]].map(
                        (round) => (
                          <Box key={`${selectedAgeShedule}_${selectedRound}_age_shedule_rounds`} mt={2}>
                            <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                              <RadioGroup
                                row
                                aria-labelledby="type_vaccination"
                                name={`age_shedule[${selectedAgeShedule}].rounds[${selectedRound}].type_vaccination`}
                                value={
                                  getSelectedCountryValue()?.age_shedule[selectedAgeShedule].rounds[selectedRound]
                                    ?.type_vaccination
                                }
                                onChange={(e: any) => handleChangeCountryData('type_vaccination', e.target.value)}
                              >
                                {(() => {
                                  const rounds =
                                    getSelectedCountryValue()?.age_shedule[selectedAgeShedule]?.rounds ?? [];
                                  const hasPriorRevaccination = rounds
                                    .slice(0, selectedRound)
                                    .some((r) => r.type_vaccination === ETypeVaccine.ReVaccination);
                                  const hasLaterVaccination = rounds
                                    .slice(selectedRound + 1)
                                    .some((r) => r.type_vaccination === ETypeVaccine.Vaccination);
                                  const reVaccinationTooltip =
                                    selectedRound === 0
                                      ? 'The first dose cannot be a revaccination'
                                      : hasLaterVaccination
                                      ? 'Change later vaccination rounds first'
                                      : '';
                                  return (
                                    <>
                                      <Tooltip
                                        title={
                                          hasPriorRevaccination ? 'Vaccination cannot follow a revaccination round' : ''
                                        }
                                        arrow
                                        disableHoverListener={!hasPriorRevaccination}
                                      >
                                        <span>
                                          <FormControlLabel
                                            value={ETypeVaccine.Vaccination}
                                            control={<Radio />}
                                            label="Vaccination"
                                            disabled={hasPriorRevaccination}
                                          />
                                        </span>
                                      </Tooltip>
                                      <Tooltip
                                        title={reVaccinationTooltip}
                                        arrow
                                        disableHoverListener={!reVaccinationTooltip}
                                      >
                                        <span>
                                          <FormControlLabel
                                            value={ETypeVaccine.ReVaccination}
                                            control={<Radio />}
                                            label="Revaccination"
                                            disabled={selectedRound === 0 || hasLaterVaccination}
                                          />
                                        </span>
                                      </Tooltip>
                                    </>
                                  );
                                })()}
                              </RadioGroup>
                              {(() => {
                                const rounds = getSelectedCountryValue()?.age_shedule[selectedAgeShedule]?.rounds ?? [];
                                const round = rounds[selectedRound];
                                const isLastRound = selectedRound === rounds.length - 1;
                                return (
                                  round?.type_vaccination === ETypeVaccine.ReVaccination &&
                                  isLastRound && (
                                    <FormControlLabel
                                      control={<Checkbox />}
                                      label="Periodically"
                                      name={`age_shedule[${selectedAgeShedule}].rounds[${selectedRound}].isPeriodicRevactination`}
                                      checked={!!round?.isPeriodicRevactination}
                                      onChange={(e: any) =>
                                        handleChangeCountryData('isPeriodicRevactination', e.target.checked)
                                      }
                                    />
                                  )
                                );
                              })()}
                            </Box>
                            <SelectInput
                              label="Type round (date vaccination)"
                              name={`type_round`}
                              value={
                                getSelectedCountryValue()?.age_shedule[selectedAgeShedule].rounds[selectedRound]
                                  ?.type_round
                              }
                              menuItem={Object.entries(ENextRound)
                                .filter((_, index) => {
                                  if (selectedRound === 0 && index < 2) return true;
                                  if (selectedRound === 1 && index > 0) return true;
                                  if (selectedRound > 1 && index > 0) return true;

                                  return false;
                                })
                                .map(([value, label]) => ({ label, value }))}
                              onChange={(_: any, value: any) => handleChangeCountryData('type_round', value)}
                            />
                            {(getSelectedCountryValue()?.age_shedule[selectedAgeShedule].rounds[selectedRound]
                              ?.type_vaccination === ETypeVaccine.Vaccination ||
                              (getSelectedCountryValue()?.age_shedule[selectedAgeShedule].rounds[selectedRound]
                                ?.type_vaccination === ETypeVaccine.ReVaccination &&
                                !getSelectedCountryValue()?.age_shedule[selectedAgeShedule].rounds[selectedRound]
                                  ?.isPeriodicRevactination)) &&
                              getSelectedCountryValue()?.age_shedule[selectedAgeShedule].rounds[selectedRound]
                                ?.type_round !== ENextRoundVariant.AnyDays && (
                                <Box display="flex" justifyContent="space-between">
                                  <TextField
                                    sx={{ width: '49%' }}
                                    margin="dense"
                                    label="Start (in days)"
                                    type="number"
                                    name={`age_shedule[${selectedAgeShedule}].rounds[${selectedRound}].count_days`}
                                    value={
                                      getSelectedCountryValue()?.age_shedule[selectedAgeShedule].rounds[selectedRound]
                                        ?.count_days ?? ''
                                    }
                                    onChange={(e: any) => handleChangeCountryData('count_days', e.target.value)}
                                  />
                                  <TextField
                                    sx={{ width: '49%' }}
                                    margin="dense"
                                    label="End (in days)"
                                    type="number"
                                    name={`age_shedule[${selectedAgeShedule}].rounds[${selectedRound}].count_days2`}
                                    value={
                                      getSelectedCountryValue()?.age_shedule[selectedAgeShedule].rounds[selectedRound]
                                        ?.count_days2 ?? ''
                                    }
                                    onChange={(e: any) => handleChangeCountryData('count_days2', e.target.value)}
                                  />
                                </Box>
                              )}
                            {getSelectedCountryValue()?.age_shedule[selectedAgeShedule].rounds[selectedRound]
                              ?.type_vaccination === ETypeVaccine.ReVaccination &&
                              getSelectedCountryValue()?.age_shedule[selectedAgeShedule].rounds[selectedRound]
                                ?.isPeriodicRevactination && (
                                <FormControl fullWidth margin="dense">
                                  <InputLabel>Period</InputLabel>
                                  <Select
                                    label="Age"
                                    value={
                                      getSelectedCountryValue()?.age_shedule[selectedAgeShedule].rounds[selectedRound]
                                        ?.count_years
                                    }
                                    name={`age_shedule[${selectedAgeShedule}].rounds[${selectedRound}].count_years`}
                                    onChange={(e: any) => handleChangeCountryData('count_years', e.target.value)}
                                  >
                                    <MenuItem value={'3M'}>Once every 3 month</MenuItem>
                                    <MenuItem value={'6M'}>Once every 6 month</MenuItem>
                                    <MenuItem value={'1y'}>Every year</MenuItem>
                                    <MenuItem value={'2y'}>Once every 2 years</MenuItem>
                                    <MenuItem value={'3y'}>Once every 3 years</MenuItem>
                                    <MenuItem value={'5y'}>Once every 5 years</MenuItem>
                                    <MenuItem value={'10y'}>Once every 10 years</MenuItem>
                                    <MenuItem value={'15y'}>Once every 15 years</MenuItem>
                                    <MenuItem value={'20y'}>Once every 20 years</MenuItem>
                                    <MenuItem value={'25y'}>Once every 25 years</MenuItem>
                                    <MenuItem value={'30y'}>Once every 30 years</MenuItem>
                                  </Select>
                                </FormControl>
                              )}
                            {(getSelectedCountryValue()?.age_shedule[selectedAgeShedule]?.rounds.length ?? 0) > 0 && (
                              <Box display="flex" justifyContent="flex-end">
                                <Button color="error" size="small" onClick={handleDeleteRound}>
                                  DELETE THIS ROUND
                                </Button>
                              </Box>
                            )}
                          </Box>
                        ),
                      )}
                  </Box>
                  {(() => {
                    const country = getSelectedCountryValue();
                    const noteStatus = country?.note_status ?? EDiseaseNoteStatus.Info;
                    const noteValue = country?.note ?? '';
                    const isAlert = noteStatus === EDiseaseNoteStatus.Alert;
                    const showAlertHint = isAlert && noteValue.trim().length === 0;
                    return (
                      <Box mt={2} ref={countryNoteSectionRef}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                          <Typography fontWeight="bold">Country note</Typography>
                          <ToggleButtonGroup
                            size="small"
                            exclusive
                            value={noteStatus}
                            onChange={(_, value) => {
                              if (value !== null) handleChangeCountryData('note_status', value);
                            }}
                            aria-label="Note status"
                            sx={{
                              '& .MuiToggleButton-root': {
                                textTransform: 'none',
                                px: 1.25,
                                py: 0.25,
                                gap: 0.5,
                              },
                              '& .MuiToggleButton-root.Mui-selected[value="alert"]': {
                                color: 'warning.dark',
                                backgroundColor: 'rgba(237, 108, 2, 0.12)',
                                '&:hover': { backgroundColor: 'rgba(237, 108, 2, 0.2)' },
                              },
                            }}
                          >
                            <ToggleButton value={EDiseaseNoteStatus.Info} aria-label="Info">
                              <InfoOutlined sx={{ fontSize: 16 }} />
                              Info
                            </ToggleButton>
                            <ToggleButton value={EDiseaseNoteStatus.Alert} aria-label="Alert">
                              <WarningAmberOutlined sx={{ fontSize: 16 }} />
                              Alert
                            </ToggleButton>
                          </ToggleButtonGroup>
                        </Box>
                        <TextField
                          fullWidth
                          multiline
                          maxRows={4}
                          margin="dense"
                          name="note"
                          placeholder="Optional country note"
                          value={noteValue}
                          onChange={(e) => handleChangeCountryData('note', e.target.value)}
                          helperText={showAlertHint ? 'Describe what needs attention' : undefined}
                          FormHelperTextProps={{ sx: { color: 'warning.main' } }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderLeft: '3px solid',
                              borderLeftColor: isAlert ? 'warning.main' : 'transparent',
                              borderTopLeftRadius: 0,
                              borderBottomLeftRadius: 0,
                              transition: 'border-left-color 0.2s',
                            },
                          }}
                        />
                      </Box>
                    );
                  })()}
                </Box>
              )}
            </Box>
            {/* <FormControlLabel
              label="Входит в список при выборе ранее перенесенных"
              control={<Checkbox />}
              name={`isIncludePastDiseases`}
              checked={formik.values.isIncludePastDiseases ?? selectedDisease?.isIncludePastDiseases}
              onChange={formik.handleChange}
            /> */}
            <TextField
              label="Descriptions (EN)"
              fullWidth
              sx={{ mt: 4 }}
              margin="dense"
              multiline
              maxRows={6}
              name="descriptions_en"
              value={formik.values.descriptions_en ?? selectedDisease?.descriptions_en}
              onChange={formik.handleChange}
            />
            <TextField
              label="Descriptions (RU)"
              fullWidth
              margin="dense"
              multiline
              maxRows={6}
              name="descriptions"
              value={formik.values.descriptions ?? selectedDisease?.descriptions}
              onChange={formik.handleChange}
            />
            <TextField
              label="Notes"
              fullWidth
              margin="dense"
              maxRows={6}
              multiline
              name="notes"
              value={formik.values.notes ?? selectedDisease?.notes}
              onChange={formik.handleChange}
            />
            {formik.touched.name && Boolean(formik.errors.name) && (
              <Typography color="error">{formik.errors.name as string}</Typography>
            )}
          </DialogContent>
          {formik.values.countries.map((item) =>
            !item.status ? (
              <Typography key={item.countryCode} color="red" textAlign="center">
                В {countriesList.find(({ code }) => code === item.countryCode)?.name} статус обязательное поле!
              </Typography>
            ) : null,
          )}
          <DialogActions>
            {userRole === EUserRole.Admin && onVerified && (
              <Button
                onClick={() => {
                  onVerified({ ...formik.values, verified: !formik.values.verified });
                  onClose();
                }}
                variant="contained"
                disabled={
                  isExistDiseaseName ||
                  codeCheck.isDuplicate ||
                  !formik.isValid ||
                  // Если верифицированная - кнопка Unverified всегда активна
                  // Если не верифицированная - применяем проверку на изменения
                  (!formik.values.verified && !isCreateMode && !formik.dirty)
                }
                color={formik.values.verified ? 'error' : 'primary'}
                sx={{ mr: 'auto' }}
              >
                {formik.values.verified ? 'Unverified' : 'Save and Verified'}
              </Button>
            )}
            <Button onClick={onClose} sx={{ mr: 8 }}>
              Cancel
            </Button>
            <LoadingButton
              type="submit"
              variant="contained"
              loading={isLoading}
              disabled={
                isExistDiseaseName ||
                codeCheck.isDuplicate ||
                !formik.isValid ||
                // Если не верифицированная - проверяем изменения
                // Если верифицированная - кнопка Save следует обычной логике (нужны изменения)
                (!isCreateMode && !formik.dirty)
              }
            >
              Save
            </LoadingButton>
          </DialogActions>
        </form>
      </Dialog>
      <CopyCountrySettingsDialog
        isOpen={isOpenCopyDialog}
        handleClose={() => setIsOpenCopyDialog(false)}
        handleCopy={handleCopyCoutrySettings}
        selectedCountryCode={selectedCountryCode}
        countriesList={countriesList}
        countries={formik.values.countries}
      />
      <Dialog
        open={emptyRoundsConfirm.open}
        onClose={handleEmptyRoundsCancel}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              backgroundColor: 'rgba(245, 158, 11, 0.12)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <WarningAmberOutlined sx={{ color: '#f59e0b', fontSize: 22 }} />
          </Box>
          <Typography variant="h6" component="span" sx={{ fontWeight: 600 }}>
            Schedule without rounds
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.7)', mb: 2 }}>
            The following schedules <strong>CONTAIN NO ROUNDS</strong>:
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
              p: 1.5,
              borderRadius: 1.5,
              backgroundColor: 'rgba(245, 158, 11, 0.08)',
              mb: 2,
            }}
          >
            {Object.entries(
              emptyRoundsConfirm.periods.reduce<Record<string, EmptyPeriodInfo[]>>((acc, p) => {
                (acc[p.countryCode] ??= []).push(p);
                return acc;
              }, {}),
            ).map(([countryCode, list]) => {
              const country = countriesList.find((c) => c.code === countryCode);
              const countryName = country?.name ?? countryCode;
              return (
                <Box key={countryCode}>
                  <Typography variant="caption" sx={{ color: '#92500a', fontWeight: 600, display: 'block', mb: 0.5 }}>
                    {countryName}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                    {list.map((p) => (
                      <Chip
                        key={`${countryCode}_${p.index}`}
                        label={`${p.start}–${p.end} days`}
                        size="small"
                        onClick={() => handleGoToEmptyPeriod(countryCode, p.index)}
                        sx={{
                          backgroundColor: 'white',
                          border: '1px dashed #f59e0b',
                          color: '#92500a',
                          fontWeight: 500,
                          borderRadius: '6px',
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              );
            })}
          </Box>
          <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.85)' }}>
            Is this intentional?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={handleEmptyRoundsCancel} sx={{ color: 'rgba(0,0,0,0.6)' }}>
            No, go back
          </Button>
          <Button
            variant="contained"
            onClick={handleEmptyRoundsConfirm}
            sx={{
              backgroundColor: '#29b194',
              ':hover': { backgroundColor: '#1f8a72' },
              textTransform: 'uppercase',
            }}
          >
            Yes, save
          </Button>
        </DialogActions>
      </Dialog>
    </Fragment>
  );
};

type CopyCountryProps = {
  isOpen: boolean;
  handleClose: () => void;
  handleCopy: (settings: TDiseaseCountry | undefined) => void;
  selectedCountryCode: string;
  countriesList: Country[];
  countries: TDiseaseCountry[];
};

const CopyCountrySettingsDialog = ({
  isOpen,
  selectedCountryCode,
  countriesList,
  countries,
  handleClose,
  handleCopy,
}: CopyCountryProps) => {
  const [code, setCode] = useState('');

  const countryList = countriesList
    ?.filter((item) => countries?.find(({ countryCode }) => item.code === countryCode))
    ?.filter((item) => item.code !== selectedCountryCode);

  const targetSettings = (): TDiseaseCountry | undefined => {
    const target = countries?.find(({ countryCode }) => code === countryCode);

    if (target)
      return {
        ...target,
        countryCode: selectedCountryCode,
      };
  };

  return (
    <Fragment>
      <Dialog open={isOpen} onClose={handleClose}>
        <DialogTitle>Copy country settings</DialogTitle>
        <DialogContent>
          <AutocompleteCountry
            label="Country"
            fullWidth
            value={code}
            countries={countryList}
            onChange={(_, value) => setCode(value.code)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            onClick={() => {
              handleCopy(targetSettings());
              handleClose();
            }}
          >
            Copy
          </Button>
        </DialogActions>
      </Dialog>
    </Fragment>
  );
};
